import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useDeleteEquipmentCategory,
  useEquipmentCategories,
  useSaveEquipmentCategory
} from '../../api/queries'
import type { EquipmentCategory } from '../../api/types'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'

/**
 * Kategorier af værktøj - hvad slags værktøj det er (beslutning 096).
 *
 * <b>En fast liste, ikke fri tekst.</b> Typen på det enkelte stykke grej er og
 * bliver fri tekst med forslag; kategorien skal man kunne filtrere på, og to
 * stavemåder af "måleudstyr" ville dele listen i to, uden at nogen opdagede det.
 *
 * <b>En kategori i brug kan ikke slettes</b> - så ville grejet stå uden. Den kan
 * slås fra i stedet: så kan man ikke vælge den mere, men det, der allerede står
 * i den, bliver.
 */
export function EquipmentCategoriesPage() {
  const { data, isLoading, error } = useEquipmentCategories(true)
  const save = useSaveEquipmentCategory()
  const remove = useDeleteEquipmentCategory()

  const [name, setName] = useState('')
  const [icon, setIcon] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  async function add(e: React.FormEvent) {
    e.preventDefault()
    setProblem(null)

    const trimmed = name.trim()
    if (!trimmed) return

    // Ny kategori laegges nederst - raekkefoelgen rettes med pilene.
    const last = Math.max(0, ...(data ?? []).map((c) => c.sortOrder))

    try {
      await save.mutateAsync({
        body: { name: trimmed, icon: icon.trim() || null, sortOrder: last + 10, isActive: true }
      })
      setName('')
      setIcon('')
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Kategorien kunne ikke gemmes.')
    }
  }

  async function toggle(category: EquipmentCategory) {
    setProblem(null)
    try {
      await save.mutateAsync({
        id: category.id,
        body: {
          name: category.name,
          icon: category.icon ?? null,
          sortOrder: category.sortOrder,
          isActive: !category.isActive
        }
      })
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Kategorien kunne ikke gemmes.')
    }
  }

  /** Flytter en kategori op eller ned ved at bytte rækkefølgetal med naboen. */
  async function move(category: EquipmentCategory, retning: -1 | 1) {
    const list = [...(data ?? [])].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'da')
    )
    const index = list.findIndex((c) => c.id === category.id)
    const neighbour = list[index + retning]
    if (!neighbour) return

    setProblem(null)
    try {
      await save.mutateAsync({
        id: category.id,
        body: {
          name: category.name,
          icon: category.icon ?? null,
          sortOrder: neighbour.sortOrder,
          isActive: category.isActive
        }
      })
      await save.mutateAsync({
        id: neighbour.id,
        body: {
          name: neighbour.name,
          icon: neighbour.icon ?? null,
          sortOrder: category.sortOrder,
          isActive: neighbour.isActive
        }
      })
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Rækkefølgen kunne ikke gemmes.')
    }
  }

  async function del(category: EquipmentCategory) {
    setProblem(null)
    if (!confirm(`Slet kategorien "${category.name}"?`)) return

    try {
      await remove.mutateAsync(category.id)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Kategorien kunne ikke slettes.')
    }
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const list = [...(data ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'da')
  )

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card title="Værktøjskategorier">
        <p className="small muted" style={{ marginTop: 0 }}>
          Kategorien siger, hvad slags værktøj det er, og man kan filtrere på den under{' '}
          <Link to="/equipment">Værktøj</Link>. Tegnet er valgfrit og står foran navnet i listerne.
        </p>

        <form className="row" style={{ gap: 8, flexWrap: 'wrap' }} onSubmit={(e) => void add(e)}>
          <input
            value={icon}
            placeholder="🔧"
            maxLength={4}
            style={{ width: 70, textAlign: 'center' }}
            onChange={(e) => setIcon(e.target.value)}
          />
          <input
            value={name}
            placeholder="F.eks. Håndværktøj"
            style={{ flex: 1, minWidth: 180 }}
            onChange={(e) => setName(e.target.value)}
          />
          <button className="btn btn--primary btn--sm" type="submit" disabled={save.isPending || !name.trim()}>
            Tilføj
          </button>
        </form>

        {problem && <ErrorBox error={problem} />}
      </Card>

      <Card>
        {list.length === 0 && (
          <EmptyState>Der er ingen kategorier endnu. Skriv den første i feltet ovenfor.</EmptyState>
        )}

        {list.length > 0 && (
          <div className="stack" style={{ gap: 8 }}>
            {list.map((c, index) => (
              <div
                key={c.id}
                className="row"
                style={{ gap: 10, alignItems: 'center', opacity: c.isActive ? 1 : 0.55 }}
              >
                <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{c.icon ?? '🧰'}</span>

                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontWeight: 600 }}>
                    {c.name}
                    {!c.isActive && <span className="tiny muted"> · slået fra</span>}
                  </div>
                  <div className="tiny muted">
                    {c.equipmentCount === 0
                      ? 'Intet værktøj i kategorien'
                      : `${c.equipmentCount} ${c.equipmentCount === 1 ? 'stykke' : 'stykker'} værktøj`}
                  </div>
                </div>

                <div className="row" style={{ gap: 4 }}>
                  <button
                    className="btn btn--sm"
                    disabled={index === 0 || save.isPending}
                    onClick={() => void move(c, -1)}
                    aria-label="Flyt op"
                  >
                    ↑
                  </button>
                  <button
                    className="btn btn--sm"
                    disabled={index === list.length - 1 || save.isPending}
                    onClick={() => void move(c, 1)}
                    aria-label="Flyt ned"
                  >
                    ↓
                  </button>
                  <button className="btn btn--sm" onClick={() => void toggle(c)}>
                    {c.isActive ? 'Slå fra' : 'Slå til'}
                  </button>
                  <button
                    className="btn btn--danger btn--sm"
                    disabled={c.equipmentCount > 0}
                    title={
                      c.equipmentCount > 0
                        ? 'Der står værktøj i kategorien. Flyt det først, eller slå kategorien fra.'
                        : undefined
                    }
                    onClick={() => void del(c)}
                  >
                    Slet
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
